// src/components/Auth/LoginPage.js

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import "./LoginPage.css";
// NOTE: The following components are assumed to exist based on AdminTasksModal.js
// If they are not available, their usage can be commented out.
import { getPasswordValidationState } from '../../utils/validation';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';

const LoginPage = () => {
  // --- State Management ---
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // --- Context and Navigation ---
  const { login } = useAuth();
  const navigate = useNavigate();

  // --- Effects ---
  // Load remembered email on initial render
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // Validate password in real-time
  useEffect(() => {
    if (password) {
      const validation = getPasswordValidationState(password);
      setPasswordValidation(validation);
    } else {
      setPasswordValidation(null);
    }
  }, [password]);

  // --- Handlers ---
  const handlePasswordFocus = () => {
    setShowPasswordRequirements(true);
  };
  const handlePasswordBlur = () => {
    // Hide requirements only if the password field is empty
    if (!password) {
      setShowPasswordRequirements(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Handle "Remember Me" functionality
    if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
    } else {
        localStorage.removeItem("rememberedEmail");
    }

    try {
      const userCredential = await login(email, password);
      const user = userCredential.user;

      if (user.emailVerified) {
        console.log("[LoginPage] Login successful. Navigating to home.");
        navigate("/");
      } else {
        console.log("[LoginPage] Login successful but email not verified.");
        setError("Please verify your email before logging in.");
        navigate("/verify-email");
      }
    } catch (error) {
      console.error("Login error:", error);
      let errorMessage = "An unexpected error occurred during login.";
      if (error.code) {
          switch (error.code) {
              case 'auth/user-not-found':
              case 'auth/wrong-password':
              case 'auth/invalid-credential':
                  errorMessage = "Invalid email or password. Please try again.";
                  break;
              case 'auth/too-many-requests':
                  errorMessage = "Access temporarily disabled due to too many failed login attempts.";
                  break;
              case 'auth/network-request-failed':
                  errorMessage = "Your network connection was lost. Please check your internet connection and try again.";
                  break;
              default:
                  errorMessage = "Failed to log in. Please check your credentials.";
          }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-form-container">
        <h2>Login</h2>
        <form onSubmit={handleSubmit} className="login-form" noValidate>
          <div className="form-group">
            <label htmlFor="loginEmail">Email</label>
            <input
              id="loginEmail"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="loginPassword">Password</label>
            <input
              id="loginPassword"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={handlePasswordFocus}
              onBlur={handlePasswordBlur}
              disabled={loading}
              required
              aria-describedby={showPasswordRequirements ? "password-reqs" : undefined}
            />
            {showPasswordRequirements && (
                <div id="password-reqs">
                    <PasswordStrengthIndicator validationState={passwordValidation} />
                </div>
            )}
          </div>

          <div className="remember-me-group">
            <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
            />
            <label htmlFor="rememberMe">Remember me</label>
          </div>

          {error && <p className="error-message">{error}</p>}

          <button type="submit" disabled={loading}>
            {loading ? "Logging In..." : "Log In"}
          </button>

          <div className="additional-actions">
            <button type="button" onClick={() => navigate("/signup")} disabled={loading}>
              Sign Up
            </button>
            <button type="button" onClick={() => navigate("/forgot-password")} disabled={loading}>
              Forgot Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;